import {
  Invoice,
  PaymentDetailsByiIdResponse,
  PaymentDetailsState,
} from '@zonofi/zono-money-store';
import { PaymentFieldKeys, PaymentModeType } from '../../../constants';
import { getFormattedAmount } from '@zonofi/common/functions/format-amount';
import { formatDate, formatTimeIST } from '../../helper';
import { defaultPaymentDetails, paymentTypeFields } from '../../constant';

interface useMappedDetailsProps {
  paymentData?: PaymentDetailsByiIdResponse;
}

export const useMappedDetails = () => {
  const detailsDataMapping = ({ paymentData }: useMappedDetailsProps) => {
    const defaultDetailsData = {
      mappedDetails: {
        paymentDetails: [],
        paymentMedia: [],
        paymentInvoice: [],
      },
    };

    if (!paymentData) return defaultDetailsData;

    const updates: Partial<PaymentDetailsState> = {};

    // Process media files
    if (paymentData.files?.length) {
      updates.paymentMedia = paymentData.files.map((file) => ({
        lastModified: Date.now(),
        uri: file.url,
        name: file.fileName,
        file: new File([], file.fileName),
        type: 'image/jpeg',
      }));
    }

    // Process invoices
    if (paymentData.paymentInvoiceMap?.length) {
      updates.paymentInvoice = paymentData.paymentInvoiceMap
        .map((item) => item.invoice)
        ?.filter(Boolean) as Invoice[];
    }

    // Payment mode from enum
    const modeKey =
      paymentData.paymentMode?.toUpperCase() as keyof typeof PaymentModeType;
    const mode =
      (PaymentModeType[modeKey] as PaymentModeType) || paymentData?.paymentMode;

    // Format time in IST
    const paymentTime = formatTimeIST(paymentData.paymentDate);

    // Additional fields based on payment mode
    const additionalFields =
      mode && paymentTypeFields[mode] ? paymentTypeFields[mode] : [];

    const paymentDetails = [...defaultPaymentDetails, ...additionalFields].map(
      (field) => {
        const responseKey = Object.keys(paymentData).find(
          (key) => key.toLowerCase() === field.key.toLowerCase()
        );

        let value =
          field.key === PaymentFieldKeys.PAYMENT_TIME
            ? paymentTime
            : responseKey
              ? paymentData[responseKey as keyof PaymentDetailsByiIdResponse]
              : 'N/A';

        switch (field.key) {
          case PaymentFieldKeys.PAYMENT_MODE:
            value = mode ? String(mode) : 'N/A';
            break;

          case PaymentFieldKeys.AMOUNT_PAID:
            value = value ? getFormattedAmount(value, true) : '';
            break;

          case PaymentFieldKeys.PAYMENT_DATE:
            value = formatDate(value);
            break;

          case PaymentFieldKeys.PAYMENT_REFERENCE_CODE:
            value = value ? value : String(Date.now());
            break;
        }

        return {
          ...field,
          value: value != null ? String(value) : 'N/A',
        };
      }
    );

    return {
      mappedDetails: {
        paymentDetails,
        paymentMedia: updates.paymentMedia || [],
        paymentInvoice: updates.paymentInvoice || [],
      },
    };
  };

  return { detailsDataMapping };
};
